import "./AddBoostFactorModal.scss"
import { BigNumber } from "@ethersproject/bignumber"
import { ArrowForwardIos } from "@mui/icons-material"
import { Box, Link } from "@mui/material"
import React, { ReactElement, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { POOLS_MAP, PoolName } from "src/constants"
import RocketIcon from "src/assets/icons/rocket.svg"
import { useAction } from "src/hooks/useAction"
import usePoolData from "src/hooks/usePoolData"
import { actions } from "src/state/xFer"
import {
  useCalculateTotalMultiplier,
  useGetBoostedNfts,
} from "src/hooks/BoostFarming/useFarmBoostContract"

import DrawerModalWrapper from "../DrawerModalWrapper"
import Text from "../Text"

const ActionItem: React.FC<{
  title: string
  subtitle?: string
  onClick: () => void
}> = ({ title, subtitle, onClick }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        border: "1px solid #009be0",
        padding: "12px",
        borderRadius: "10px",
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <Box>
        <Text fontWeight="500" size="small">
          {title}
        </Text>
        {subtitle && (
          <Text color="dim" size="small">
            {subtitle}
          </Text>
        )}
      </Box>
      <ArrowForwardIos fontSize="small" />
    </Box>
  )
}

interface Props {
  open: boolean
  onClose: () => void
  poolName: PoolName
}
export default function IncreaseBoostModal({
  open,
  onClose,
  poolName,
}: Props): ReactElement {
  const navigate = useNavigate()
  const pool = POOLS_MAP[poolName]
  const [, userShareData, boostData] = usePoolData(poolName)
  const { data: totalMultiplier } = useCalculateTotalMultiplier()
  const { data: stakedNftData } = useGetBoostedNfts()
  const stakedNftIds = stakedNftData ?? []
  const openDepositModal = useAction(actions.setOpenedDepositModal, {
    defaultSelectedPool: null,
  })
  const openNftStakeModal = useAction(actions.setOpenedNftStakeModal, {
    nftIds: stakedNftIds,
  })
  const onUpgradeVault = useCallback(() => {
    navigate("/stake/vault")
    onClose()
  }, [navigate, onClose])
  const onStakeMoreXFER = useCallback(() => {
    openDepositModal()
    onClose()
  }, [openDepositModal, onClose])
  const onNFTStake = useCallback(() => {
    openNftStakeModal()
    onClose()
  }, [openNftStakeModal, onClose])
  const goToStake = useCallback(() => {
    const action = userShareData?.lpTokenBalance.gt(0) ? "stake" : "deposit"
    navigate(`/pools/${pool.route}/${action}`)
    onClose()
  }, [navigate, onClose, pool.route, userShareData?.lpTokenBalance])
  const avgMultiplier =
    boostData &&
    boostData.totalAdjustLpAmount &&
    userShareData &&
    userShareData.stakedAmount.gt(0)
      ? boostData.totalAdjustLpAmount
          .mul(userShareData.stakedShare)
          .div(userShareData.stakedAmount)
          .div(BigNumber.from(10).pow(18))
      : BigNumber.from(1)
  const link = "https://docs.ferroprotocol.com/user-guides/pool-boost"

  return (
    <DrawerModalWrapper title="" open={open} onClose={onClose}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Box
          my={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text mb="4px" variant="h4" maxWidth="300px">
            To earn potential MAX boosted APR
          </Text>
          <img src={RocketIcon} width={48} />
        </Box>
        <Text>
          {
            "You can earn higher boosted APR by staking more xFER into our vault with longer duration, and staking additional NFTs. (Up to 3X Boost Multiplier). Take your action now!"
          }
        </Text>
        <Box mt={1} mb={3} display="flex" flexDirection="column" gap="20px">
          <ActionItem
            title="Stake More xFER To xFER Vault"
            onClick={onStakeMoreXFER}
          />
          <ActionItem
            title="Upgrade xFER Vault Lock Duration"
            onClick={onUpgradeVault}
          />
          <ActionItem
            title="Stake More Cronos Cruisers NFT"
            onClick={onNFTStake}
          />
          {!!totalMultiplier && avgMultiplier.gt(totalMultiplier) && (
            <ActionItem
              title="Add And Stake More Liquidity To The Pool"
              onClick={goToStake}
            />
          )}
        </Box>
        <Link fontSize="14px" color="#fff" target="_blank" href={link}>
          How can I estimate how my deposit increase the boost power?
        </Link>
      </Box>
    </DrawerModalWrapper>
  )
}
